/**
 * Wires the framework-free SyncClient (core) to the IDB store and the HTTP API,
 * and drives it from the triggers in spec 08 (start, online, debounce, interval).
 */
import { SyncClient, type SyncStore, type SyncApi, type ServerChange } from '@boatnav/core';
import { createStore } from 'zustand/vanilla';
import { api } from '../../lib/apiClient.js';
import {
  collectDirty,
  markSynced,
  applyServerChange,
  getSyncCursor,
  setSyncCursor,
  markAllDirty,
} from '../../lib/db.js';
import { useAuthStore, setOnAuthenticated } from './authStore.js';
import { applyServerSettings } from './settingsSync.js';

export interface SyncUiState {
  lastSyncedAt: number | null;
  syncing: boolean;
  error: boolean;
}
export const syncUiStore = createStore<SyncUiState>(() => ({
  lastSyncedAt: null,
  syncing: false,
  error: false,
}));

// 'settings' (spec 17) is a whole-doc LWW row applied through the six pref
// stores' own setters, not IDB directly — routed to its bridge here so the
// engine (and applyServerChange's boat/route/track dispatch) stays generic.
// Exported for direct dispatch testing.
export function applyServer(change: ServerChange): Promise<void> {
  if (change.entity === 'settings') return applyServerSettings(change);
  // Rebuild the object: TS narrows `change.entity` on read, but not the
  // `change` reference itself when passed on whole (a known TS limitation).
  const { entity, id, updatedAt, deleted, payload } = change;
  return applyServerChange({ entity, id, updatedAt, deleted, payload });
}

const store: SyncStore = {
  collectDirty,
  markSynced,
  applyServer,
  getCursor: getSyncCursor,
  setCursor: setSyncCursor,
};

const apiAdapter: SyncApi = {
  push: (changes) => api.syncPush(changes) as ReturnType<SyncApi['push']>,
  pull: (since, limit) =>
    api.syncPull(since, limit) as Promise<{ changes: ServerChange[]; nextSince: number; more: boolean }>,
};

const client = new SyncClient(store, apiAdapter);

let debounce: ReturnType<typeof setTimeout> | null = null;
let interval: ReturnType<typeof setInterval> | null = null;
let started = false;

function loggedIn(): boolean {
  return useAuthStore.getState().status === 'authed';
}

export async function syncNow(): Promise<void> {
  if (!loggedIn() || !navigator.onLine) return;
  syncUiStore.setState({ syncing: true, error: false });
  try {
    await client.sync();
    syncUiStore.setState({ syncing: false, lastSyncedAt: Date.now(), error: false });
  } catch {
    syncUiStore.setState({ syncing: false, error: true });
  }
}

/** Debounced trigger after a local mutation (spec 08: 10 s). */
export function notifyMutation(): void {
  if (!loggedIn()) return;
  if (debounce) clearTimeout(debounce);
  debounce = setTimeout(() => void syncNow(), 10_000);
}

export function startSyncTriggers(): void {
  if (started) return;
  started = true;

  // First login merges local anonymous data up (spec 06).
  setOnAuthenticated(() => {
    void markAllDirty().then(() => syncNow());
  });

  window.addEventListener('online', () => void syncNow());
  interval = setInterval(() => {
    if (document.visibilityState === 'visible') void syncNow();
  }, 15 * 60_000);

  // Kick once on start (no-op if logged out).
  void syncNow();
}

export function stopSyncTriggers(): void {
  if (interval) clearInterval(interval);
  interval = null;
  started = false;
}
